<template>
  <!-- Primary card: header, controls, and pinned chart (or full grid when no pinned). Sits in col 1 row 1 to share its row with the sidebar on lg+. -->
  <div
    class="bg-surface-0 dark:bg-surface-900 dark:border-surface-700 rounded-lg p-6 min-w-0 sm:col-span-2 min-[1440px]:col-span-1 min-[1440px]:col-start-1 min-[1440px]:row-start-1"
    data-test-id="merchant-analytics"
  >
    <!-- Section title -->
    <h3 class="text-sm font-semibold text-surface-900 dark:text-surface-0 mb-3">
      Merchant Analytics
    </h3>

    <!-- Controls row: chart filter dropdown (left) + date pickers (right) -->
    <div class="flex flex-wrap items-end justify-between gap-3 mb-5">
      <!-- Chart filter dropdown trigger -->
      <div class="flex flex-col gap-1" data-test-id="chart-filter-picker">
        <span class="text-xs text-surface-500 dark:text-surface-400 uppercase">
          Charts
        </span>
        <button
          type="button"
          class="inline-flex items-center justify-between gap-3 min-w-[220px] px-3 py-1.5 text-sm rounded border border-surface-300 dark:border-surface-700 bg-surface-0 dark:bg-surface-900 text-surface-900 dark:text-surface-0 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
          data-test-id="chart-filter-trigger"
          @click="toggleChartsMenu"
        >
          <span class="flex items-center gap-2">
            <i
              class="pi pi-chart-line text-xs text-surface-500 dark:text-surface-400"
            />
            <span>
              <span class="font-medium">{{ activeChartIds.size }}</span>
              <span class="text-surface-500 dark:text-surface-400">
                of {{ CHART_SPECS.length }} selected</span
              >
            </span>
          </span>
          <i
            class="pi pi-chevron-down text-[10px] text-surface-500 dark:text-surface-400"
          />
        </button>
        <Popover ref="chartsMenu" data-test-id="chart-filter-popover">
          <ul class="flex flex-col gap-0.5 min-w-[240px] py-1">
            <li v-for="chart in CHART_SPECS" :key="chart.id">
              <label
                class="flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                :data-test-id="`chart-toggle-${chart.id}`"
              >
                <Checkbox
                  :modelValue="activeChartIds.has(chart.id)"
                  binary
                  @update:modelValue="toggleChart(chart.id)"
                />
                <span class="text-sm text-surface-900 dark:text-surface-0">
                  {{ chart.title }}
                </span>
              </label>
            </li>
          </ul>
        </Popover>
      </div>

      <!-- Date range pickers -->
      <div class="flex items-end gap-2">
        <div class="flex flex-col gap-1">
          <label
            for="analytics-date-from"
            class="text-xs text-surface-500 dark:text-surface-400 uppercase"
            >From</label
          >
          <input
            id="analytics-date-from"
            v-model="dateFrom"
            type="date"
            data-test-id="analytics-date-from"
            class="px-2 py-1 text-sm rounded border border-surface-300 dark:border-surface-700 bg-surface-0 dark:bg-surface-900 text-surface-900 dark:text-surface-0"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="analytics-date-to"
            class="text-xs text-surface-500 dark:text-surface-400 uppercase"
            >To</label
          >
          <input
            id="analytics-date-to"
            v-model="dateTo"
            type="date"
            data-test-id="analytics-date-to"
            class="px-2 py-1 text-sm rounded border border-surface-300 dark:border-surface-700 bg-surface-0 dark:bg-surface-900 text-surface-900 dark:text-surface-0"
          />
        </div>
      </div>
    </div>

    <!-- No merchant selected -->
    <div
      v-if="!merchantId"
      class="text-sm text-surface-500 dark:text-surface-400"
    >
      Select a merchant to view analytics.
    </div>

    <template v-else>
      <!-- Pinned chart: shares its row with the alerts sidebar on lg+ -->
      <div v-if="pinnedChart" class="h-[520px]">
        <AnalyticsChart
          :spec="pinnedChart"
          :merchant-id="merchantId"
          :date-from="dateFrom"
          :date-to="dateTo"
          :pinned="true"
          @pin="pinnedChartId = null"
        />
      </div>

      <!-- No pinned chart: render all visible charts in a 2-col grid inside this card -->
      <div
        v-else-if="visibleCharts.length > 0"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-[400px]"
      >
        <AnalyticsChart
          v-for="chart in visibleCharts"
          :key="chart.id"
          :spec="chart"
          :merchant-id="merchantId"
          :date-from="dateFrom"
          :date-to="dateTo"
          :pinned="false"
          @pin="pinChart(chart.id)"
        />
      </div>

      <!-- Pinned with nothing else to show, or nothing selected at all -->
      <div v-else class="text-sm text-surface-500 dark:text-surface-400">
        No additional charts selected. Use the filters above to show more.
      </div>
    </template>
  </div>

  <!-- Secondary card: only when a chart is pinned; spans both columns on lg+ so it flows under the sidebar. -->
  <div
    v-if="merchantId && pinnedChart && visibleCharts.length > 0"
    class="bg-surface-0 dark:bg-surface-900 shadow-sm rounded-lg p-6 sm:col-span-2 min-[1440px]:col-span-full min-[1440px]:row-start-2 min-w-0"
    data-test-id="merchant-analytics-secondary"
  >
    <div
      class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 auto-rows-[260px]"
    >
      <AnalyticsChart
        v-for="chart in visibleCharts"
        :key="chart.id"
        :spec="chart"
        :merchant-id="merchantId"
        :date-from="dateFrom"
        :date-to="dateTo"
        :pinned="false"
        @pin="pinChart(chart.id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Popover from 'primevue/popover'
import Checkbox from 'primevue/checkbox'
import AnalyticsChart from '@/components/fcc/charts/AnalyticsChart.vue'
import { CHART_SPECS } from '@/util/fcc/charts/chartSpecs'
import { defaultDateRange } from '@/util/fcc/charts/dateRange'
import type { Merchant } from '@/types/fcc'

interface Props {
  merchant: Merchant | null
}

const props = defineProps<Props>()

const initialRange = defaultDateRange(120)
const dateFrom = ref(initialRange.date_from)
const dateTo = ref(initialRange.date_to)

const merchantId = computed(() =>
  props.merchant?.MERCHANT_ID != null ? String(props.merchant.MERCHANT_ID) : '',
)

const activeChartIds = ref(
  new Set(['volume', 'auth-rate', 'refund-rate', 'chargebacks']),
)
const pinnedChartId = ref<string | null>(null)

const chartsMenu = ref<InstanceType<typeof Popover> | null>(null)
const toggleChartsMenu = (event: Event) => {
  chartsMenu.value?.toggle(event)
}

const pinnedChart = computed(() =>
  pinnedChartId.value
    ? (CHART_SPECS.find(c => c.id === pinnedChartId.value) ?? null)
    : null,
)

const visibleCharts = computed(() =>
  CHART_SPECS.filter(
    c => activeChartIds.value.has(c.id) && c.id !== pinnedChartId.value,
  ),
)

const toggleChart = (id: string) => {
  if (id === pinnedChartId.value) pinnedChartId.value = null
  const next = new Set(activeChartIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  activeChartIds.value = next
}

const pinChart = (id: string) => {
  pinnedChartId.value = pinnedChartId.value === id ? null : id
}
</script>
